import { pagesApi, layoutsApi, sitesApi } from '../../../api/client';
import type { Page } from '../../../schemas/validation';
import type { SiteManagerState } from '../useSiteManagerState';
import { appendPageLinksToLayout, pagePath } from './appendPageLinksToLayout';

async function resolveLayoutToUpdate(siteId: string, layoutId: string | null) {
  if (layoutId) {
    return layoutsApi.get(layoutId);
  }
  const { layout } = await layoutsApi.getDefault(siteId);
  if (layout) return layout;
  const { layouts } = await layoutsApi.list(siteId);
  return layouts.find(l => l.isDefault) || layouts[0] || null;
}

interface PageHandlerDependencies {
  siteId: string | undefined;
  state: SiteManagerState;
}

export const createPageHandlers = ({ siteId, state }: PageHandlerDependencies) => {
  const {
    pages,
    setPages,
    setShowCreatePageModal,
    setIsCreatingPage,
    setShowEditPageModal,
    setPageToEdit,
    setShowDeleteModal,
    pageToDelete,
    setPageToDelete,
    setIsDeletingPage,
  } = state;

  const applyLayoutForNewPage = async (
    page: Page,
    title: string,
    slug: string,
    isHomePage: boolean,
    layoutId: string | null,
    showLayout: boolean,
    addToNav: boolean,
    addToFooter: boolean
  ) => {
    if (!siteId) return;

    if (!showLayout) {
      try {
        await layoutsApi.setPageLayout(page.id, { layoutId: null, showLayout: false });
        page.layoutId = undefined;
        page.showLayout = false;
      } catch (error) {
        console.error('Failed to set page layout:', error);
      }
      return;
    }

    let layout = null;
    try {
      layout = await resolveLayoutToUpdate(siteId, layoutId);
    } catch (error) {
      console.error('Failed to resolve layout for new page:', error);
    }

    const resolvedId = layoutId || layout?.id || null;
    try {
      await layoutsApi.setPageLayout(page.id, { layoutId: resolvedId, showLayout: true });
      page.layoutId = resolvedId ?? undefined;
      page.showLayout = true;
    } catch (error) {
      console.error('Failed to set page layout:', error);
    }

    if (!layout || (!addToNav && !addToFooter)) return;

    try {
      const components = appendPageLinksToLayout(layout.components, {
        title,
        url: pagePath(slug, isHomePage),
        addToNav,
        addToFooter,
      });
      if (components.length === 0) return;

      await layoutsApi.update(layout.id, { components });
      await layoutsApi.publish(layout.id);
    } catch (error) {
      console.error('Failed to add page to navigation/footer:', error);
    }
  };

  const handleCreatePage = async (
    title: string,
    slug: string,
    isHomePage = false,
    noindex = false,
    layoutId: string | null = null,
    showLayout = true,
    addToNav = false,
    addToFooter = false
  ) => {
    try {
      setIsCreatingPage(true);

      try {
        const newPage = await pagesApi.create(siteId!, {
          title,
          slug,
          orderPosition: pages.length,
          isHomePage,
          noindex,
        });

        await applyLayoutForNewPage(
          newPage,
          title,
          slug,
          isHomePage,
          layoutId,
          showLayout,
          addToNav,
          addToFooter
        );

        setPages(prev => [...prev, newPage]);
        setShowCreatePageModal(false);
      } catch (apiError) {
        console.error('API create failed, trying fallback:', apiError);

        const mockPage: Page = {
          id: `mock-${Date.now()}`,
          siteId: siteId!,
          title,
          slug,
          components: JSON.stringify({ ROOT: { type: 'div', nodes: [] } }),
          orderPosition: pages.length,
          layoutId: layoutId || undefined,
          showLayout: layoutId ? showLayout : true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        const existingPages = JSON.parse(localStorage.getItem(`pages-${siteId}`) || '[]');
        existingPages.push(mockPage);
        localStorage.setItem(`pages-${siteId}`, JSON.stringify(existingPages));

        setPages(prev => [...prev, mockPage]);
        setShowCreatePageModal(false);

        alert(
          'Page created locally. Note: This is a fallback since the backend pages API is not yet implemented.'
        );
      }
    } catch (error: unknown) {
      console.error('Failed to create page:', error);
      const apiError = error as { message?: string; status?: number; data?: unknown };
      let errorMessage = 'Failed to create page.';
      if (error instanceof Error) {
        errorMessage += ` Error: ${error.message}`;
      }
      if (apiError?.status) {
        errorMessage += ` (Status: ${apiError.status})`;
      }
      if (apiError?.data) {
        errorMessage += ` Details: ${JSON.stringify(apiError.data)}`;
      }
      alert(errorMessage);
    } finally {
      setIsCreatingPage(false);
    }
  };

  const handleCreatePageFromTemplate = async (
    title: string,
    slug: string,
    templateId: string,
    isHomePage = false,
    noindex = false,
    layoutId: string | null = null,
    showLayout = true,
    addToNav = false,
    addToFooter = false
  ) => {
    try {
      setIsCreatingPage(true);

      const newPage = await pagesApi.createFromTemplate(siteId!, {
        templateId,
        title,
        slug,
        orderPosition: pages.length,
        isHomePage,
        noindex,
      });

      await applyLayoutForNewPage(
        newPage,
        title,
        slug,
        isHomePage,
        layoutId,
        showLayout,
        addToNav,
        addToFooter
      );

      setPages(prev => [...prev, newPage]);
      setShowCreatePageModal(false);
    } catch (error: unknown) {
      console.error('Failed to create page from template:', error);
      const apiError = error as { message?: string; status?: number; data?: unknown };
      let errorMessage = 'Failed to create page from template.';
      if (error instanceof Error) {
        errorMessage += ` Error: ${error.message}`;
      }
      if (apiError?.status) {
        errorMessage += ` (Status: ${apiError.status})`;
      }
      if (apiError?.data) {
        errorMessage += ` Details: ${JSON.stringify(apiError.data)}`;
      }
      alert(errorMessage);
    } finally {
      setIsCreatingPage(false);
    }
  };

  const handleDeletePage = async (pageId: string) => {
    try {
      setIsDeletingPage(true);
      await pagesApi.delete(pageId);
      setPages(prev => prev.filter(p => p.id !== pageId));
      setShowDeleteModal(false);
      setPageToDelete(null);
    } catch (error) {
      console.error('Failed to delete page:', error);
      alert('Failed to delete page.');
    } finally {
      setIsDeletingPage(false);
    }
  };

  const handleEditPageClick = (page: Page) => {
    setPageToEdit(page);
    setShowEditPageModal(true);
  };

  const handleEditPageConfirm = (updatedPage: Page) => {
    setPages(prevPages => prevPages.map(page => (page.id === updatedPage.id ? updatedPage : page)));
    setPageToEdit(null);
    setShowEditPageModal(false);
  };

  const handleEditPageCancel = () => {
    setPageToEdit(null);
    setShowEditPageModal(false);
  };

  const handleDeletePageClick = (page: Page) => {
    setPageToDelete(page);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    if (pageToDelete) {
      handleDeletePage(pageToDelete.id);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setPageToDelete(null);
  };

  const handleToggleHomePage = async (page: Page) => {
    try {
      // If this page is already the home page, clear it; otherwise set it
      const newHomePageId = page.isHomePage ? null : page.id;
      await sitesApi.setHomePage(siteId!, newHomePageId);

      // Update local state: mark the new home page and clear the old one
      setPages(prevPages =>
        prevPages.map(p => ({
          ...p,
          isHomePage: newHomePageId ? p.id === newHomePageId : false,
        }))
      );

      const updatedPages = pages.map(p => ({
        ...p,
        isHomePage: newHomePageId ? p.id === newHomePageId : false,
      }));
      localStorage.setItem(`pages-${siteId}`, JSON.stringify(updatedPages));
    } catch (error) {
      console.error('Failed to update home page:', error);
      alert('Failed to update home page setting. Please try again.');
    }
  };

  return {
    handleCreatePage,
    handleCreatePageFromTemplate,
    handleEditPageClick,
    handleEditPageConfirm,
    handleEditPageCancel,
    handleDeletePageClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleToggleHomePage,
  };
};
